/**
 * FilterBar — controlled status-chip row + free-text search (Wave 2b).
 *
 * Presentational: receives the active {@link BoardFilter} and emits a new one
 * on every user action. No fetch, no polling, no global reads — the parent
 * owns all state. Designed to be dropped into both the Pipeline view (cross-run
 * host-lens trials) and, later, any single-run board, because the underlying
 * {@link filterTrials} logic is already cross-run.
 *
 * Vocabulary follows the operator-facing rename from the spec (§2B):
 *   queued → Waiting, leased → Claimed, running → Running,
 *   recovering → Recovering, done → Done.
 */

import type { JSX } from 'react'
import { type BoardFilter, isLaneActive, nextActiveLanes } from './board-concurrency'
import { LANES, type LaneId } from './board-lanes'

/** Operator-vocabulary display labels for the five pipeline lanes (spec §2B). */
const CHIP_LABELS: Readonly<Record<LaneId, string>> = {
  queued: 'Waiting',
  leased: 'Claimed',
  running: 'Running',
  recovering: 'Recovering',
  done: 'Done',
}

const ALL_LANE_IDS: readonly LaneId[] = LANES.map((l) => l.id)

export interface FilterBarProps {
  /** The current filter state (activeLanes + query). */
  filter: BoardFilter
  /** Called whenever the user changes any filter control. */
  onChange: (next: BoardFilter) => void
  /**
   * Override the search input placeholder text.
   * Defaults to the trial-fidelity label when unset.
   */
  placeholder?: string
  /**
   * Override the accessible label on the search input.
   * Should be updated alongside `placeholder` — screen readers use aria-label,
   * not placeholder, so both must match the current fidelity context.
   */
  ariaLabel?: string
  /**
   * When true, the lane-status chip row is hidden.
   * Use in run fidelity where trial-lane vocabulary is misleading.
   */
  hideLaneChips?: boolean
}

/**
 * A controlled filter bar: five lane-status chips and a free-text search input.
 * Chip clicks toggle lane visibility via the shared {@link nextActiveLanes} logic;
 * text input sets the query. Both compose: a trial must pass both the lane
 * filter and the query to appear.
 */
export function FilterBar({ filter, onChange, placeholder, ariaLabel, hideLaneChips }: FilterBarProps): JSX.Element {
  const handleLaneToggle = (laneId: LaneId): void => {
    onChange({ ...filter, activeLanes: nextActiveLanes(filter.activeLanes, laneId, ALL_LANE_IDS) })
  }

  const handleQueryChange = (query: string): void => {
    onChange({ ...filter, query })
  }

  return (
    <div className="filter-bar" data-testid="filter-bar">
      {!hideLaneChips && (
        <fieldset className="filter-chips" aria-label="filter by lane" data-testid="filter-chips">
          {LANES.map((lane) => (
            <button
              key={lane.id}
              type="button"
              className={isLaneActive(filter, lane.id) ? 'filter-chip filter-chip-on' : 'filter-chip filter-chip-off'}
              data-testid={`filter-chip-${lane.id}`}
              data-on={isLaneActive(filter, lane.id) ? 'true' : 'false'}
              aria-pressed={isLaneActive(filter, lane.id)}
              onClick={() => handleLaneToggle(lane.id)}
            >
              {CHIP_LABELS[lane.id]}
            </button>
          ))}
        </fieldset>
      )}
      <div className="filter-search-wrap">
        <input
          className="filter-search"
          type="search"
          placeholder={placeholder ?? 'filter trial / scenario / arm'}
          aria-label={ariaLabel ?? 'filter trials'}
          data-testid="filter-search"
          value={filter.query}
          onChange={(e) => handleQueryChange(e.currentTarget.value)}
        />
        {filter.query !== '' && (
          <button
            type="button"
            className="filter-search-clear"
            aria-label="clear search"
            data-testid="filter-search-clear"
            onClick={() => handleQueryChange('')}
          >
            ×
          </button>
        )}
      </div>
    </div>
  )
}
